'use client';

/**
 * ManagedSelect — the one dropdown for every field with a set of values
 * (New Draft owner rule 2). Single or multi-select, searchable, and the
 * list is the org's: "+ Add …" adds a value inline, a pencil edits and a
 * bin removes the values people added, an admin hides built-ins from
 * "Manage values". Values persist on the server per org
 * (`/option-lists/{key}`, or the catalogue's own endpoints through an
 * adapter); built-ins never delete.
 *
 *     <ManagedSelect listKey="party_roles" value={roles} onChange={setRoles} multiple
 *                    aria-label="Party roles" />
 *
 * Keyboard: type to filter · ↑/↓ move · Enter picks (or opens "+ Add") ·
 * Escape closes or cancels · Backspace on an empty box removes the last
 * chip · Tab reaches the row's edit and delete buttons.
 *
 * What the server refuses (a duplicate, a role without `add`) is said
 * inline with the server's own sentence; nothing is written locally first.
 * A value the record holds but the list no longer offers (archived, hidden)
 * stays as a chip with its label, marked "no longer offered".
 */
import { Check, ChevronDown, Pencil, Plus, Settings2, Trash2, X } from 'lucide-react';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/components/ui/Button';
import { cn } from '@/components/ui/cn';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import {
  canAdd as canAddTo,
  canManage,
  chosenLabels,
  exactMatch,
  formFields,
  nextValue,
  unresolvedKeys,
  validateInput,
  valueKeys,
  visibleItems,
  withoutKey,
  writeErrorMessage,
} from '@/lib/optionLists';
import { useOptionList, useOptionListMutations, useResolvedOptions } from '@/lib/optionLists/hooks';
import type {
  OptionItem,
  OptionList,
  OptionListAdapter,
  OptionValueInput,
  PayloadFieldSpec,
} from '@/lib/optionLists/types';

export { useOptionList } from '@/lib/optionLists/hooks';

export interface ManagedSelectProps {
  listKey: string;
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Offer "+ Add …" (when the person's role may add). Default true. */
  allowAdd?: boolean;
  adapter?: OptionListAdapter;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  className?: string;
  /** Keys not to offer here (chosen in a sibling field). */
  excludeKeys?: readonly string[];
  /** Called with a value added inline (it is also selected). */
  onAdded?: (item: OptionItem) => void;
  /** Open on first render (stories, tests). */
  defaultOpen?: boolean;
}

type Mode =
  | { kind: 'list' }
  | { kind: 'add' }
  | { kind: 'edit'; item: OptionItem }
  | { kind: 'delete'; item: OptionItem };

interface DraftForm {
  label: string;
  payload: Record<string, string>;
}

const emptyDraft = (label = ''): DraftForm => ({ label, payload: {} });

function payloadOf(item: OptionItem): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(item.payload ?? {})) if (typeof v === 'string') out[k] = v;
  return out;
}

export function ManagedSelect({
  listKey,
  value,
  onChange,
  multiple = false,
  placeholder,
  disabled = false,
  allowAdd = true,
  adapter,
  id,
  className,
  excludeKeys,
  onAdded,
  defaultOpen = false,
  ...aria
}: ManagedSelectProps) {
  const autoId = useId();
  const inputId = id ?? `${autoId}-input`;
  const listId = `${autoId}-list`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [draft, setDraft] = useState<DraftForm>(emptyDraft());
  const [formError, setFormError] = useState<Record<string, string>>({});
  const [manageOpen, setManageOpen] = useState(false);

  const listQ = useOptionList(listKey, { adapter });
  const list = listQ.data;
  const muts = useOptionListMutations(listKey, adapter);

  const keys = valueKeys(value);
  const missing = unresolvedKeys(list, keys);
  const resolvedQ = useResolvedOptions(listKey, missing, missing.length > 0 && !adapter);
  const chips = chosenLabels(list, keys, resolvedQ.data ?? {});

  const exclude = useMemo(() => new Set(excludeKeys ?? []), [excludeKeys]);
  const rows = useMemo(
    () => visibleItems(list, query).filter((i) => !exclude.has(i.key) || keys.includes(i.key)),
    [list, query, exclude, keys],
  );
  const addable = canAddTo(list, allowAdd) && muts.supports.create;
  const offerAdd = addable && !exactMatch(list, query);
  const addIndex = offerAdd ? rows.length : -1;
  const rowCount = rows.length + (offerAdd ? 1 : 0);
  const busy =
    muts.create.isPending || muts.update.isPending || muts.archive.isPending || muts.setHidden.isPending;

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => setActive(0), [query]);

  function close() {
    setOpen(false);
    setMode({ kind: 'list' });
    setFormError({});
  }

  function pick(item: OptionItem) {
    const next = nextValue(value, item.key, multiple);
    onChange(next);
    if (!multiple) {
      setQuery('');
      close();
    }
  }

  function startAdd() {
    setDraft(emptyDraft(query.trim()));
    setFormError({});
    setMode({ kind: 'add' });
  }

  function startEdit(item: OptionItem) {
    setDraft({ label: item.label, payload: payloadOf(item) });
    setFormError({});
    setMode({ kind: 'edit', item });
  }

  async function submitAdd() {
    if (!list) return;
    const input: OptionValueInput = {
      label: draft.label.trim(),
      ...(Object.keys(draft.payload).length ? { payload: cleanPayload(draft.payload) } : {}),
    };
    const errs = validateInput(list, input);
    if (Object.keys(errs).length) return setFormError(errs);
    try {
      const item = await muts.create.mutateAsync(input);
      onChange(nextValue(value, item.key, multiple));
      onAdded?.(item);
      setQuery('');
      setMode({ kind: 'list' });
      if (!multiple) close();
      inputRef.current?.focus();
    } catch (err) {
      setFormError({ form: writeErrorMessage(err) });
    }
  }

  async function submitEdit(item: OptionItem) {
    if (!list) return;
    const input: OptionValueInput = { label: draft.label.trim(), payload: cleanPayload(draft.payload) };
    const errs = validateInput(list, input, { editingKey: item.key });
    if (Object.keys(errs).length) return setFormError(errs);
    try {
      await muts.update.mutateAsync({
        item,
        patch: {
          label: input.label,
          ...(list.payload_fields.length ? { payload: input.payload } : {}),
        },
      });
      setMode({ kind: 'list' });
      inputRef.current?.focus();
    } catch (err) {
      setFormError({ form: writeErrorMessage(err) });
    }
  }

  async function confirmDelete(item: OptionItem) {
    try {
      await muts.archive.mutateAsync(item);
      setMode({ kind: 'list' });
      inputRef.current?.focus();
    } catch (err) {
      setFormError({ form: writeErrorMessage(err) });
    }
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (mode.kind !== 'list') return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((i) => Math.min(Math.max(rowCount - 1, 0), i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Home' && open) {
      setActive(0);
    } else if (e.key === 'End' && open) {
      setActive(Math.max(rowCount - 1, 0));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      if (active === addIndex) startAdd();
      else if (rows[active]) pick(rows[active]);
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        close();
      }
    } else if (e.key === 'Backspace' && !query && multiple && keys.length) {
      const last = keys[keys.length - 1];
      if (last) onChange(withoutKey(value, last, multiple));
    } else if (e.key === 'Tab') {
      // Leaving forward reaches the row buttons only while open; closing on
      // Shift+Tab keeps focus order sane.
      if (e.shiftKey) close();
    }
  };

  const single = !multiple ? chips[0] : null;
  const optionId = (i: number) => `${autoId}-opt-${i}`;
  const ph = placeholder ?? (list ? `Choose ${list.label.toLowerCase()}…` : 'Choose…');

  return (
    <div
      ref={rootRef}
      className={cn('relative', className)}
      data-list-key={listKey}
      onBlur={(e) => {
        // Focus left the whole control (Tab past the last row button).
        const next = e.relatedTarget as Node | null;
        if (open && !manageOpen && next && !rootRef.current?.contains(next)) close();
      }}
    >
      <div
        className={cn(
          'flex min-h-10 w-full flex-wrap items-center gap-1 rounded-md border border-slate-300 bg-white py-1 pl-2 pr-16 text-sm focus-within:border-action focus-within:ring-2 focus-within:ring-gold',
          disabled && 'cursor-not-allowed bg-slate-50',
          aria['aria-invalid'] && 'border-state-danger',
        )}
        onClick={() => {
          if (disabled) return;
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {multiple
          ? chips.map((c) => (
              <span
                key={c.key}
                className={cn(
                  'inline-flex max-w-full items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-fg',
                  c.inactive && 'italic text-fg-muted',
                )}
                title={c.inactive ? 'No longer offered in this list' : undefined}
              >
                <span className="truncate">{c.label}</span>
                {c.inactive ? <span className="sr-only"> (no longer offered)</span> : null}
                {!disabled ? (
                  <button
                    aria-label={`Remove ${c.label}`}
                    className="rounded hover:bg-slate-200"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(withoutKey(value, c.key, multiple));
                    }}
                  >
                    <X aria-hidden size={12} />
                  </button>
                ) : null}
              </span>
            ))
          : null}
        <input
          ref={inputRef}
          aria-activedescendant={open && mode.kind === 'list' && active < rowCount ? optionId(active) : undefined}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-describedby={aria['aria-describedby']}
          aria-expanded={open}
          aria-invalid={aria['aria-invalid'] || undefined}
          aria-label={aria['aria-label']}
          autoComplete="off"
          className={cn(
            'h-8 min-w-[6rem] flex-1 border-0 bg-transparent px-1 text-sm text-fg placeholder:text-fg-muted focus:outline-none disabled:cursor-not-allowed',
            single?.inactive && 'italic',
          )}
          disabled={disabled}
          id={inputId}
          placeholder={single ? single.label : keys.length ? '' : ph}
          role="combobox"
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <div className="absolute inset-y-0 right-2 flex items-center gap-1 text-fg-muted">
          {listQ.isLoading ? <Spinner aria-label="Loading options" size={13} /> : null}
          {!multiple && single && !disabled ? (
            <button
              aria-label="Clear selection"
              className="rounded p-1 hover:bg-slate-100 hover:text-fg"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            >
              <X aria-hidden size={13} strokeWidth={2} />
            </button>
          ) : null}
          <ChevronDown aria-hidden size={14} strokeWidth={1.75} />
        </div>
      </div>

      {open && !disabled ? (
        // `data-popover-open` lifts the Card holding it above the cards after
        // it (components/ui/Card.tsx), or the next card covers the list.
        <div
          className="absolute left-0 right-0 top-full z-dropdown mt-1 rounded-lg border border-slate-200 bg-white p-1 shadow-3"
          data-popover-open=""
        >
          {listQ.isError ? (
            <div className="flex items-center justify-between gap-2 px-2.5 py-2 text-sm" role="alert">
              <span className="text-state-danger">The list could not be loaded.</span>
              <Button size="sm" variant="secondary" onClick={() => void listQ.refetch()}>
                Retry
              </Button>
            </div>
          ) : null}

          {mode.kind === 'add' && list ? (
            <ValueForm
              busy={muts.create.isPending}
              draft={draft}
              errors={formError}
              fields={formFields(list)}
              list={list}
              submitLabel="Add"
              title={list.add_label}
              onCancel={() => setMode({ kind: 'list' })}
              onChange={setDraft}
              onSubmit={() => void submitAdd()}
            />
          ) : (
            <ul aria-label={list?.label} aria-multiselectable={multiple || undefined} className="max-h-64 overflow-y-auto" id={listId} role="listbox">
              {listQ.isLoading ? <li className="px-2.5 py-2 text-sm text-fg-muted">Loading…</li> : null}
              {list && rows.length === 0 && !offerAdd ? (
                <li className="px-2.5 py-2 text-sm text-fg-muted">
                  {query ? 'No matches.' : 'Nothing to choose yet.'}
                </li>
              ) : null}
              {rows.map((item, i) => {
                const selected = keys.includes(item.key);
                if (mode.kind === 'edit' && mode.item.key === item.key && list) {
                  return (
                    <li key={item.key} aria-selected={selected} role="option">
                      <ValueForm
                        busy={muts.update.isPending}
                        draft={draft}
                        errors={formError}
                        fields={formFields(list)}
                        list={list}
                        submitLabel="Save"
                        title={`Edit “${item.label}”`}
                        onCancel={() => setMode({ kind: 'list' })}
                        onChange={setDraft}
                        onSubmit={() => void submitEdit(item)}
                      />
                    </li>
                  );
                }
                if (mode.kind === 'delete' && mode.item.key === item.key) {
                  return (
                    <li key={item.key} aria-selected={selected} className="rounded-md bg-error-50 px-2.5 py-2" role="option">
                      <p className="text-sm text-fg">
                        Remove “{item.label}” from this list? Anything that already uses it keeps it, and it can
                        be restored from Manage values.
                      </p>
                      {formError.form ? <p className="mt-1 text-xs text-state-danger">{formError.form}</p> : null}
                      <div className="mt-2 flex justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setMode({ kind: 'list' })}>
                          Cancel
                        </Button>
                        <Button
                          isLoading={muts.archive.isPending}
                          size="sm"
                          variant="destructive"
                          onClick={() => void confirmDelete(item)}
                        >
                          Remove
                        </Button>
                      </div>
                    </li>
                  );
                }
                return (
                  <li
                    key={item.key}
                    aria-selected={selected}
                    className={cn(
                      'group flex cursor-pointer items-start justify-between gap-2 rounded-md px-2.5 py-1.5',
                      i === active && 'bg-slate-100',
                    )}
                    id={optionId(i)}
                    role="option"
                    onClick={() => pick(item)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                  >
                    <span className="flex min-w-0 items-start gap-2">
                      {multiple ? (
                        <span
                          aria-hidden
                          className={cn(
                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            selected ? 'border-action bg-action text-action-fg' : 'border-slate-300',
                          )}
                        >
                          {selected ? <Check size={11} strokeWidth={3} /> : null}
                        </span>
                      ) : null}
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-fg">{item.label}</span>
                        {item.description ? (
                          <span className="block truncate text-xs text-fg-muted">{item.description}</span>
                        ) : null}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-0.5">
                      {!item.builtin ? <span className="text-[10px] uppercase tracking-wide text-fg-muted">Firm</span> : null}
                      {item.can_edit && muts.supports.update ? (
                        <button
                          aria-label={`Edit ${item.label}`}
                          className="rounded p-1 text-fg-muted hover:bg-slate-200 hover:text-fg"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEdit(item);
                          }}
                        >
                          <Pencil aria-hidden size={12} />
                        </button>
                      ) : null}
                      {item.can_delete && muts.supports.archive ? (
                        <button
                          aria-label={`Delete ${item.label}`}
                          className="rounded p-1 text-fg-muted hover:bg-error-50 hover:text-state-danger"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormError({});
                            setMode({ kind: 'delete', item });
                          }}
                        >
                          <Trash2 aria-hidden size={12} />
                        </button>
                      ) : null}
                      {!multiple && selected ? <Check aria-hidden className="text-gold-text" size={14} /> : null}
                    </span>
                  </li>
                );
              })}
              {offerAdd ? (
                <li
                  aria-selected={false}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-md border-t border-slate-100 px-2.5 py-1.5 text-sm font-medium text-gold-text',
                    active === addIndex && 'bg-slate-100',
                  )}
                  id={optionId(addIndex)}
                  role="option"
                  onClick={startAdd}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(addIndex)}
                >
                  <Plus aria-hidden size={14} strokeWidth={2} />
                  <span className="truncate">
                    {query.trim() ? `Add “${query.trim()}”` : `${list?.add_label ?? 'Add value'}…`}
                  </span>
                </li>
              ) : null}
            </ul>
          )}

          {list && mode.kind === 'list' ? (
            <div className="mt-1 flex items-center justify-between gap-2 border-t border-slate-100 px-2 pt-1 text-xs">
              {list.managed_by === 'clause_library' && list.manage_href ? (
                <a className="text-gold-text hover:underline" href={list.manage_href}>
                  Manage in the Clause Library
                </a>
              ) : (
                <span className="text-fg-muted">{busy ? 'Saving…' : `${rows.length} shown`}</span>
              )}
              {canManage(list) ? (
                <button
                  className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-fg-secondary hover:bg-slate-100"
                  type="button"
                  onClick={() => setManageOpen(true)}
                >
                  <Settings2 aria-hidden size={12} /> Manage values
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {manageOpen ? (
        <ManageValuesModal adapter={adapter} listKey={listKey} onClose={() => setManageOpen(false)} />
      ) : null}
    </div>
  );
}

function cleanPayload(p: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v.trim()) out[k] = v.trim();
  return out;
}

interface ValueFormProps {
  list: OptionList;
  title: string;
  draft: DraftForm;
  fields: PayloadFieldSpec[];
  errors: Record<string, string>;
  busy: boolean;
  submitLabel: string;
  onChange: (d: DraftForm) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

/** The inline add / edit form: a name, then the list's own details. */
function ValueForm({ list, title, draft, fields, errors, busy, submitLabel, onChange, onSubmit, onCancel }: ValueFormProps) {
  const base = useId();
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    } else if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement)) {
      e.preventDefault();
      onSubmit();
    }
  };
  return (
    <div aria-label={title} className="space-y-2 p-2" role="group" onKeyDown={onKey}>
      <p className="text-xs font-semibold text-fg">{title}</p>
      <Labelled error={errors.label} htmlFor={`${base}-label`} label={list.single_word ? 'Word' : 'Name'}>
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          aria-invalid={Boolean(errors.label) || undefined}
          className="h-8 w-full rounded border border-slate-300 px-2 text-sm focus:border-action focus:outline-none focus:ring-2 focus:ring-gold"
          id={`${base}-label`}
          maxLength={list.single_word ? 60 : 200}
          value={draft.label}
          onChange={(e) => onChange({ ...draft, label: e.target.value })}
        />
      </Labelled>
      {fields.map((f) => {
        const fid = `${base}-${f.name}`;
        const err = errors[`payload.${f.name}`];
        const v = draft.payload[f.name] ?? '';
        const set = (nv: string) => onChange({ ...draft, payload: { ...draft.payload, [f.name]: nv } });
        return (
          <Labelled key={f.name} error={err} htmlFor={fid} label={`${f.label}${f.required ? '' : ' (optional)'}`}>
            {f.choices.length ? (
              <select
                className="h-8 w-full rounded border border-slate-300 px-2 text-sm"
                id={fid}
                value={v}
                onChange={(e) => set(e.target.value)}
              >
                <option value="">Choose…</option>
                {f.choices.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            ) : f.max_len > 120 ? (
              <textarea
                className="min-h-[64px] w-full rounded border border-slate-300 px-2 py-1 text-sm"
                id={fid}
                maxLength={f.max_len}
                value={v}
                onChange={(e) => set(e.target.value)}
              />
            ) : (
              <input
                className="h-8 w-full rounded border border-slate-300 px-2 text-sm"
                id={fid}
                maxLength={f.max_len}
                value={v}
                onChange={(e) => set(e.target.value)}
              />
            )}
          </Labelled>
        );
      })}
      {errors.form ? (
        <p className="text-xs text-state-danger" role="alert">
          {errors.form}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button isLoading={busy} size="sm" onClick={onSubmit}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

function Labelled({ label, htmlFor, error, children }: { label: string; htmlFor: string; error?: string; children: ReactNode }) {
  return (
    <div>
      <label className="mb-0.5 block text-xs text-fg-secondary" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? <p className="mt-0.5 text-xs text-state-danger">{error}</p> : null}
    </div>
  );
}

/** Manage values: hide or show built-ins, restore or remove the org's own. */
export function ManageValuesModal({
  listKey,
  adapter,
  onClose,
}: {
  listKey: string;
  adapter?: OptionListAdapter;
  onClose: () => void;
}) {
  const q = useOptionList(listKey, { adapter, includeHidden: true, includeArchived: true });
  const muts = useOptionListMutations(listKey, adapter);
  const [error, setError] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const list = q.data;
  const builtins = list?.items.filter((i) => i.builtin) ?? [];
  const own = list?.items.filter((i) => !i.builtin) ?? [];
  const canHide = Boolean(list?.permissions.can_hide_builtins && list.allow_hide && muts.supports.setHidden);

  async function run(key: string, fn: () => Promise<unknown>) {
    setError(null);
    setPendingKey(key);
    try {
      await fn();
    } catch (err) {
      setError(writeErrorMessage(err));
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <Modal
      open
      description="Hidden built-ins and removed values stay on anything that already uses them."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
      size="lg"
      title={`Manage values — ${list?.label ?? 'list'}`}
      onClose={onClose}
    >
      {q.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-fg-muted">
          <Spinner size={14} /> Loading…
        </p>
      ) : q.isError ? (
        <div className="flex items-center justify-between gap-2 text-sm" role="alert">
          <span className="text-state-danger">The list could not be loaded.</span>
          <Button size="sm" variant="secondary" onClick={() => void q.refetch()}>
            Retry
          </Button>
        </div>
      ) : list ? (
        <div className="space-y-5">
          {error ? (
            <p className="text-sm text-state-danger" role="alert">
              {error}
            </p>
          ) : null}
          <section>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">Your organisation’s values</h3>
            {own.length === 0 ? (
              <p className="text-sm text-fg-muted">None yet. Add one from the dropdown’s “+ {list.add_label}”.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded border border-slate-200">
                {own.map((i) => (
                  <li key={i.key} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className={cn('min-w-0 truncate', i.archived && 'italic text-fg-muted')}>
                      {i.label}
                      {i.archived ? ' — removed' : ''}
                    </span>
                    {i.archived ? (
                      i.can_delete && muts.supports.restore ? (
                        <Button
                          isLoading={pendingKey === i.key}
                          size="sm"
                          variant="secondary"
                          onClick={() => void run(i.key, () => muts.restore.mutateAsync(i))}
                        >
                          Restore
                        </Button>
                      ) : null
                    ) : i.can_delete && muts.supports.archive ? (
                      <Button
                        isLoading={pendingKey === i.key}
                        size="sm"
                        variant="secondary"
                        onClick={() => void run(i.key, () => muts.archive.mutateAsync(i))}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {list.allow_hide ? (
            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">Built-in values</h3>
              {!canHide ? (
                <p className="mb-1 text-xs text-fg-muted">Only an admin, or a role that may edit, can hide built-ins.</p>
              ) : null}
              <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded border border-slate-200">
                {builtins.map((i) => (
                  <li key={i.key} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className={cn('min-w-0 truncate', i.hidden && 'text-fg-muted line-through')}>{i.label}</span>
                    {canHide && i.can_hide !== false ? (
                      <Button
                        isLoading={pendingKey === i.key}
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          void run(i.key, () => muts.setHidden.mutateAsync({ item: i, hidden: !i.hidden }))
                        }
                      >
                        {i.hidden ? 'Show' : 'Hide'}
                      </Button>
                    ) : i.hidden ? (
                      <span className="text-xs text-fg-muted">Hidden</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
